import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Award, Globe } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To provide world-class connectivity solutions that empower individuals and businesses to thrive in the digital age.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'We prioritize customer satisfaction above all, ensuring reliable service and dedicated support 24/7.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality internet and entertainment services with cutting-edge technology.'
    },
    {
      icon: Globe,
      title: 'Innovation',
      description: 'Continuously evolving our services to meet the changing needs of our connected world.'
    }
  ];

  const stats = [
    { number: '50+', label: 'Cities Covered' },
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '1 Gbps', label: 'Max Speed' },
    { number: '24/7', label: 'Customer Support' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-red-600">i5World</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              i5World is a leading provider of high-speed internet, OTT IPTV, and roaming WiFi
              solutions. With a commitment to excellence and innovation, we're connecting India
              to the digital future.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-red-600 text-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-red-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg mx-auto">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Founded with a vision to transform digital connectivity in India, i5World has grown
                from a small startup to a trusted name in the telecommunications industry. Our
                journey began with a simple belief: everyone deserves access to fast, reliable, and
                affordable internet.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Today, we serve customers across 50+ cities, providing not just internet
                connectivity, but a complete digital lifestyle experience. From high-speed fiber
                internet to comprehensive OTT IPTV services and portable WiFi solutions, we're
                committed to keeping you connected wherever you are.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our team of dedicated professionals works tirelessly to ensure that every customer
                receives the best possible service. We invest in cutting-edge technology, maintain a
                robust network infrastructure, and provide round-the-clock support because your
                connectivity matters to us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon size={32} className="text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose i5World?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Reliable Network</h3>
                <p className="text-gray-700">
                  Our fiber-optic infrastructure ensures 99.9% uptime and consistent speeds, even
                  during peak hours.
                </p>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Service</h3>
                <p className="text-gray-700">
                  No hidden charges, no surprises. What you see is what you get - honest pricing and
                  clear terms.
                </p>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced Technology</h3>
                <p className="text-gray-700">
                  We use the latest networking equipment and technologies to deliver superior
                  performance.
                </p>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Support</h3>
                <p className="text-gray-700">
                  Our dedicated support team is available 24/7 to assist you with any questions or
                  issues.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
